import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, LayoutDashboard, Globe, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFarmStore } from '@/store/useFarmStore';
import { useAuthStore } from '@/store/useAuthStore';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { detectCountryCode, type SupportedCountry } from '@/lib/currency';
import { useCurrency } from '@/hooks/use-currency';
import { useFxRates } from '@/hooks/use-fx-rates';
import { cn } from '@/lib/utils';

export function Navbar() {
  const cart = useFarmStore((s) => s.cart);
  const selectedCountry = useFarmStore((s) => s.selectedCountry);
  const setCountry = useFarmStore((s) => s.setCountry);
  const { role, isAuthenticated } = useAuthStore();
  const { clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);
  const { countries } = useCurrency();
  const { lastUpdatedRelative, isLoading: fxLoading } = useFxRates();
  
  const currentCountry = selectedCountry || detectCountryCode();
  const currentOption = countries.find(c => c.code === currentCountry) || countries[0];
  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/logo-mark.png" alt="FarmPack AI" className="h-8 w-8 object-contain" />
              <span className="font-bold text-lg tracking-tight hidden sm:inline">FarmPack</span>
            </Link>
            <div className="hidden md:flex items-center gap-5">
              <Link to="/catalog" className="text-sm font-medium text-muted-foreground hover:text-foreground">Catalog</Link>
              <Link to="/wizard" className="text-sm font-medium text-muted-foreground hover:text-foreground">Wizard</Link>
              {isAuthenticated && role === 'admin' && (
                <Link to="/admin" className="text-xs font-medium text-muted-foreground/60 hover:text-primary flex items-center gap-1">
                  <LayoutDashboard className="h-3 w-3" /> Admin
                </Link>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={currentOption?.code || currentCountry} onValueChange={(value) => setCountry(value as SupportedCountry)}>
              <SelectTrigger className="h-8 w-[160px] text-xs border-border">
                <Globe className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue>{currentOption?.flag} {currentOption?.name}</SelectValue>
                <span className="ml-auto text-[10px] text-muted-foreground hidden lg:inline">
                  {fxLoading ? '...' : lastUpdatedRelative}
                </span>
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code} className="text-xs">
                    <div className="flex items-center justify-between w-full">
                      <span>{country.flag} {country.name}</span>
                      <span className="text-muted-foreground ml-2">({country.currency})</span>
                    </div>
                  </SelectItem>
                ))}
                <div className="px-2 py-1.5 text-[10px] text-muted-foreground border-t">
                  FX rates: {lastUpdatedRelative || 'Never updated'}
                </div>
              </SelectContent>
            </Select>
            <ThemeToggle className="relative top-0 right-0 h-8 w-8" />
            <Button
              variant="ghost"
              size="icon"
              className="relative h-8 w-8"
              onClick={() => navigate('/cart')}
            >
              <ShoppingCart className="h-4 w-4" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                  {cartCount}
                </span>
              )}
            </Button>
            <Button asChild size="sm" className="hidden sm:flex rounded-md bg-primary hover:bg-primary/90">
              <Link to="/wizard">Get Started</Link>
            </Button>
            {isAuthenticated ? (
              <>
                <Button variant="ghost" size="sm" asChild className="text-xs">
                  <Link to="/account">Account</Link>
                </Button>
                <Button variant="outline" size="sm" onClick={() => { clearAuth(); navigate('/'); }} className="text-xs">
                  Logout
                </Button>
              </>
            ) : (
              <Button asChild size="sm" variant="outline" className="hidden sm:flex rounded-md">
                <Link to="/login">Login</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}